import { Avatar, Loader, Text } from "@mantine/core";

import { useListMembers } from "../../../api/generated/endpoints/teams/teams";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { memberName } from "../../../platform/members";
import { DetailModal } from "../../../ui/DetailModal";
import { formatDay } from "../../../ui/format";
import { GenerationDetail } from "./GenerationDetail";
import { useJob } from "./job-query";

export interface GenerationDetailModalProps {
  teamId: string;
  jobId: string;
  onClose: () => void;
}

/**
 * A job's detail over the page it was opened from (the studio's rows). Render it while a job is
 * chosen. The header is live's `AssetInfoHeader` (issue 252 item 5, design-ref/gaps/37): the
 * maker's initials in a round avatar, their name ("Unknown" when the team no longer has them)
 * and the day it was made.
 */
export function GenerationDetailModal({ teamId, jobId, onClose }: GenerationDetailModalProps) {
  const job = useJob(teamId, jobId);
  // A members list that cannot be read names no one: "Unknown", as live without a maker.
  const listed = useListMembers(teamId);
  const members = listed.isError ? [] : listed.data;
  const maker = members?.find((member) => member.user_id === job.data?.created_by);
  const name = memberName(maker);
  const header =
    job.data === undefined || members === undefined ? null : (
      <>
        <Avatar size={40} radius="xl" color="indigo" name={name} />
        <div>
          <Text fw={500}>{name}</Text>
          <Text size="sm">{formatDay(job.data.created_at)}</Text>
        </div>
      </>
    );
  return (
    <DetailModal opened onClose={onClose} label="Generation" header={header}>
      {job.isPending ? <Loader size="sm" aria-label="Loading the job" /> : null}
      {job.isError ? <ErrorAlert error={job.error} /> : null}
      {job.isSuccess ? (
        <GenerationDetail
          teamId={teamId}
          job={job.data}
          readAgain={() => void job.refetch()}
          headingOrder={2}
        />
      ) : null}
    </DetailModal>
  );
}
